/** Google Ads account used by amberpeak.ca, thehivekingston.com, and app.amberpeak.ca. */
export const GOOGLE_ADS_ID = 'AW-17684180781';

/**
 * Google counts a conversion only when the event name is `conversion` and send_to includes the action label.
 * Live with Us is the bidding conversion. The three tenant-app actions are secondary.
 */
export const GOOGLE_ADS_LIVE_WITH_US_CONVERSION_SEND_TO = `${GOOGLE_ADS_ID}/8uIdCNiyyLEdEK3evPBB`;
export const GOOGLE_ADS_DISCOVERY_CALL_BOOKED_CONVERSION_SEND_TO = `${GOOGLE_ADS_ID}/JUQhCKLHgbIdEK3evPBB`;
export const GOOGLE_ADS_TOUR_BOOKED_CONVERSION_SEND_TO = `${GOOGLE_ADS_ID}/9P3eCITm-LEdEK3evPBB`;
export const GOOGLE_ADS_APPLICATION_SUBMITTED_CONVERSION_SEND_TO = `${GOOGLE_ADS_ID}/TxqiCLbp-bEdEK3evPBB`;

const GOOGLE_ADS_CONVERSION_EVENT = 'conversion';

/** Click ids Google appends to the ad landing URL. Must survive the hop into the prospect portal. */
export const GOOGLE_ADS_CLICK_ID_PARAMS = ['gclid', 'gbraid', 'wbraid'] as const;

export type GoogleAdsClickIdParam = (typeof GOOGLE_ADS_CLICK_ID_PARAMS)[number];

export type GoogleAdsClickIds = Partial<Record<GoogleAdsClickIdParam, string>>;

/**
 * Event names recorded in the product.
 * The four labeled actions are sent to Google as conversions. Profile complete stays unlabeled.
 */
export const GOOGLE_ADS_EVENTS = {
  LIVE_WITH_US_CLICK: 'live_with_us_click',
  DISCOVERY_CALL_BOOKED: 'discovery_call_booked',
  TOUR_BOOKED: 'tour_booked',
  APPLICATION_SUBMITTED: 'application_submitted',
  PROSPECT_PROFILE_COMPLETE: 'prospect_profile_complete',
} as const;

/** Browser memory so a later visit does not send the same event again. */
export const GOOGLE_ADS_ONCE_KEYS = {
  LIVE_WITH_US_CLICK: 'amber-peak-google-ads-live-with-us-clicked',
  DISCOVERY_CALL_BOOKED: 'amber-peak-google-ads-discovery-call-booked',
  TOUR_BOOKED: 'amber-peak-google-ads-tour-booked',
  APPLICATION_SUBMITTED: 'amber-peak-google-ads-application-submitted',
} as const;

export const GOOGLE_ADS_LINKER = {
  domains: ['amberpeak.ca', 'thehivekingston.com'],
  accept_incoming: true,
} as const;

const GOOGLE_ADS_HOSTS = new Set([
  'amberpeak.ca',
  'www.amberpeak.ca',
  'thehivekingston.com',
  'www.thehivekingston.com',
  'app.amberpeak.ca',
]);

export function isGoogleAdsMeasurementHost(hostname: string | undefined): boolean {
  if (!hostname) return false;
  return GOOGLE_ADS_HOSTS.has(hostname);
}

export function readGoogleAdsClickIds(search: string): GoogleAdsClickIds {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(raw);
  const ids: GoogleAdsClickIds = {};
  for (const key of GOOGLE_ADS_CLICK_ID_PARAMS) {
    const value = params.get(key)?.trim();
    if (value) ids[key] = value;
  }
  return ids;
}

export function serializeGoogleAdsClickIds(ids: GoogleAdsClickIds): string {
  const params = new URLSearchParams();
  for (const key of GOOGLE_ADS_CLICK_ID_PARAMS) {
    const value = ids[key];
    if (value) params.set(key, value);
  }
  return params.toString();
}

/** Copy ad click ids onto a destination URL. Does not overwrite ids already set. */
export function appendGoogleAdsClickIds(url: URL, search: string): void {
  const ids = readGoogleAdsClickIds(search);
  for (const key of GOOGLE_ADS_CLICK_ID_PARAMS) {
    const value = ids[key];
    if (value && !url.searchParams.get(key)) {
      url.searchParams.set(key, value);
    }
  }
}

/** Keep click ids on an in-app path such as `/my-information` after Auth0 returns. */
export function applyGoogleAdsClickIdsToPath(
  path: string,
  search: string,
  origin = 'https://app.amberpeak.ca',
): string {
  const url = new URL(path, origin);
  appendGoogleAdsClickIds(url, search);
  return `${url.pathname}${url.search}${url.hash}`;
}

interface GoogleAdsOnceStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

interface GoogleAdsBrowser {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
  location?: { hostname?: string };
  localStorage?: GoogleAdsOnceStorage;
}

const GOOGLE_ADS_CONVERSION_SEND_TO: Record<string, string> = {
  [GOOGLE_ADS_EVENTS.LIVE_WITH_US_CLICK]: GOOGLE_ADS_LIVE_WITH_US_CONVERSION_SEND_TO,
  [GOOGLE_ADS_EVENTS.DISCOVERY_CALL_BOOKED]: GOOGLE_ADS_DISCOVERY_CALL_BOOKED_CONVERSION_SEND_TO,
  [GOOGLE_ADS_EVENTS.TOUR_BOOKED]: GOOGLE_ADS_TOUR_BOOKED_CONVERSION_SEND_TO,
  [GOOGLE_ADS_EVENTS.APPLICATION_SUBMITTED]: GOOGLE_ADS_APPLICATION_SUBMITTED_CONVERSION_SEND_TO,
};

/** Labeled actions are sent as Google conversions. Unlabeled events stay on the account tag. */
export function googleAdsEventDispatch(eventName: string): { eventName: string; sendTo: string } {
  const sendTo = GOOGLE_ADS_CONVERSION_SEND_TO[eventName];
  if (sendTo) {
    return { eventName: GOOGLE_ADS_CONVERSION_EVENT, sendTo };
  }
  return { eventName, sendTo: GOOGLE_ADS_ID };
}

/** Shared compiles without the DOM library, so the browser is reached through globalThis. */
function googleAdsBrowser(): GoogleAdsBrowser | null {
  const scope = globalThis as GoogleAdsBrowser;
  if (!scope.location) return null;
  return scope;
}

/**
 * Install the command queue Google's tag actually reads.
 * A rest-parameter array is ignored, so config and conversion commands never leave the browser.
 */
export function ensureGoogleAdsGtag(browser: {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
}): void {
  browser.dataLayer = browser.dataLayer ?? [];
  if (browser.gtag) return;
  const dataLayer = browser.dataLayer;
  browser.gtag = function gtag() {
    dataLayer.push(arguments);
  };
}

/**
 * Queue a Google Ads event. The tag on each site sends it once loaded.
 * Ignored off the three production hosts so local Docker does not pollute the account.
 */
export function trackGoogleAdsEvent(
  eventName: string,
  params?: Record<string, string | number | undefined>,
): void {
  const browser = googleAdsBrowser();
  if (!browser) return;
  if (!isGoogleAdsMeasurementHost(browser.location?.hostname)) return;

  ensureGoogleAdsGtag(browser);

  const dispatch = googleAdsEventDispatch(eventName);
  const payload: Record<string, string | number> = {};
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && key !== 'send_to') payload[key] = value;
    }
  }
  payload.send_to = dispatch.sendTo;
  browser.gtag('event', dispatch.eventName, payload);
}

/** Returns true the first time this browser should send the event. */
export function claimGoogleAdsEventOnce(memoryKey: string, storage: GoogleAdsOnceStorage | null): boolean {
  if (!storage) return true;
  try {
    if (storage.getItem(memoryKey) === '1') return false;
    storage.setItem(memoryKey, '1');
    return true;
  } catch {
    return true;
  }
}

/** Send a Google Ads event once per browser. Clearing site data allows it to send again. */
export function trackGoogleAdsEventOnce(
  memoryKey: string,
  eventName: string,
  params?: Record<string, string | number | undefined>,
): void {
  const browser = googleAdsBrowser();
  if (!browser?.localStorage) return;
  if (!isGoogleAdsMeasurementHost(browser.location?.hostname)) return;
  if (!claimGoogleAdsEventOnce(memoryKey, browser.localStorage)) return;
  trackGoogleAdsEvent(eventName, params);
}
